import ButtonBaseWithSelectionMenu from '@components/ButtonBaseWithSelectionMenu/ButtonBaseWithSelectionMenu';
import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import { Box, Stack } from '@mui/material';
import { Plus } from 'lucide-react';
import { ReactNode } from 'react';
import useWidth from '../../../../../components/Hooks/useWidth';
import LangEnum from '../../../../../lang/LangEnum';
import { AddOrderProductTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import ColorElement from '../../../../products/colors/ColorElement';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useOrderProductsLayoutContext } from '../../../contexts/OrderProductsLayout.context';
import { useOrderProductItemStyles } from '../../../styles/useOrderProductItemStyles';
import { ORDER_PRODUCT_SELECTION_MENU_CONFIG } from '../../orderProducts.constants';
import { OrderProductGridItem } from '../../orderProducts.types';
import { OrderProductRowLayout } from '../../orderProductsLayout.types';
import { isOrderProductRowInline } from '../../orderProductsLayout.utils';
import OrderProductButtonContent from '../../product/components/OrderProductButtonContent';
import OrderProductPriceV2 from '../../product/components/OrderProductPriceV2';
import useOrderProductGridButtonStyles from '../../product/grid/components/orderProductGrid/components/buttons/styles/useOrderProductGridButtonStyles';
import AddOrderProductCodeField from './AddOrderProductCodeField';
import AddOrderProductColorMenu from './AddOrderProductColorMenu';
import {
  addRowMobileGridRowSx,
  addRowTotalsSlotSx,
  getAddRowButtonSlotSx,
  getAddRowButtonsSx,
  getAddRowGridSx,
  getAddRowInfoSx,
  getAddRowSx,
  getAddRowTotalsWrapperSx,
  getCodeFieldBoxSx,
} from './addOrderProductCodeField.styles';
import {
  AddOrderProductRowAddButtonConfig,
  AddOrderProductRowColorConfig,
  AddOrderProductRowProductConfig,
  AddOrderProductRowTotalsConfig,
} from './addOrderProductRow.types';

export interface AddOrderProductRowViewProps {
  product: AddOrderProductRowProductConfig;
  color: AddOrderProductRowColorConfig;
  totals: AddOrderProductRowTotalsConfig;
  addButtonConfig: AddOrderProductRowAddButtonConfig;
  deliveryWindowSlot: ReactNode;
  gridSlot: ReactNode;
  grid: OrderProductGridItem[];
}

interface AddOrderProductSubmitButtonProps {
  config: AddOrderProductRowAddButtonConfig;
  label: string;
  hasTouchLayout: boolean;
}

const AddOrderProductSubmitButton = ({
  config,
  label,
  hasTouchLayout,
}: AddOrderProductSubmitButtonProps) => {
  const { classes } = useOrderProductGridButtonStyles();
  const size = hasTouchLayout ? 'large' : 'medium';
  const className = hasTouchLayout ? classes.endMobileBaseButton : undefined;

  if (config.show && config.isSubmitting) {
    return <Loading size={24} />;
  }

  return (
    <IconButton
      icon={Plus}
      size={size}
      className={className}
      disabled={!config.show}
      testId={AddOrderProductTestIds.SUBMIT_BUTTON}
      ariaLabel={label}
      onClick={config.onClick}
      sx={{
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { color: 'primary.contrastText', backgroundColor: 'primary.dark' },
        '&.Mui-disabled': { backgroundColor: 'action.disabledBackground' },
      }}
    />
  );
};

const AddOrderProductRowView = ({
  product,
  color,
  totals,
  addButtonConfig,
  deliveryWindowSlot,
  gridSlot,
  grid,
}: AddOrderProductRowViewProps) => {
  const { layout, hasTouchGrid } = useOrderProductsLayoutContext();
  const isMobileLayout = layout === OrderProductRowLayout.MOBILE;
  const isInlineLayout = isOrderProductRowInline(layout);
  const { mediumDownWidth: hasTouchLayout } = useWidth();
  const { translate } = useTranslation();
  const { classes } = useOrderProductItemStyles({ layout, hasTouchGrid });

  const totalsContent = grid.length > 0 && (
    <Box sx={{ flexShrink: 0 }}>
      <OrderProductPriceV2 grid={grid} partsQuantity={totals.partsQuantity} total={totals.total} />
    </Box>
  );

  const addButton = (
    <Box sx={getAddRowButtonSlotSx(hasTouchLayout)}>
      <AddOrderProductSubmitButton
        config={addButtonConfig}
        label={translate(LangEnum.GLOBAL_ADD)}
        hasTouchLayout={hasTouchLayout}
      />
    </Box>
  );

  const gridBox = (
    <Box
      className={classes.orderProductGridContainer}
      sx={getAddRowGridSx(layout)}
      test-id={AddOrderProductTestIds.PRODUCT_GRID}
    >
      {gridSlot}
    </Box>
  );

  const totalsAndActions = (
    <Box
      className={classes.orderProductTotalsAndActionsWrapper}
      sx={getAddRowTotalsWrapperSx(layout)}
    >
      <div className={classes.orderProductTotalsAndActionsContainer}>
        {totalsContent}
        {addButton}
      </div>
    </Box>
  );

  return (
    <Stack className={classes.root} direction="row" sx={getAddRowSx(layout)}>
      <div className={classes.orderProductInfoContainer}>
        <Stack className={classes.orderProductInfo} sx={getAddRowInfoSx(layout)}>
          <Box className={classes.orderProductImageAndLabel} sx={getCodeFieldBoxSx(layout)}>
            <AddOrderProductCodeField {...product} />
          </Box>

          <Box className={classes.orderProductButtons} sx={getAddRowButtonsSx(layout)}>
            <div className={classes.orderProductColor}>
              <ButtonBaseWithSelectionMenu
                viewMode={SelectionMenuViewMode.EDIT}
                isFocusable
                disabled={color.isColorDisabled}
                isLoadingButton={false}
                menuConfig={ORDER_PRODUCT_SELECTION_MENU_CONFIG}
                testId={AddOrderProductTestIds.COLOR_AUTOCOMPLETE}
                buttonContent={
                  color.selectedColor ? (
                    <OrderProductButtonContent
                      prefix={color.selectedColor.code}
                      label={color.selectedColor.name}
                      disabled={color.isColorDisabled}
                      startAdornment={
                        <ColorElement
                          size={1.75}
                          isImage={color.selectedColor.isImage}
                          hexcode={color.selectedColor.hexcode}
                          imageUrl={color.selectedColor.imageUrl}
                        />
                      }
                    />
                  ) : (
                    <OrderProductButtonContent
                      label={translate(LangEnum.GLOBAL_COLOR)}
                      disabled={color.isColorDisabled}
                    />
                  )
                }
                renderMenuContent={({ closeMenu }) => (
                  <AddOrderProductColorMenu
                    colors={color.colors}
                    selectedColorId={color.selectedColor?.id}
                    onSelect={(selectedColorOption) => {
                      color.onSelectColor(selectedColorOption);
                      closeMenu();
                    }}
                  />
                )}
              />
            </div>

            <div className={classes.orderProductDeliveryWindow}>{deliveryWindowSlot}</div>
          </Box>

          {!isInlineLayout && !isMobileLayout && (
            <Box className={classes.orderProductMobileTotals} sx={addRowTotalsSlotSx}>
              {totalsContent}
              {addButton}
            </Box>
          )}
        </Stack>
      </div>

      {isMobileLayout ? (
        <Box sx={addRowMobileGridRowSx}>
          {gridBox}
          {(grid.length > 0 || addButtonConfig.show) && totalsAndActions}
        </Box>
      ) : (
        <>
          {gridBox}
          {isInlineLayout && totalsAndActions}
        </>
      )}
    </Stack>
  );
};

export default AddOrderProductRowView;
